import React, { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import apiService from "../../api/axiosInstance";
import ParentAuthLayout from "./ParentAuthLayout";
import { useDispatch } from "react-redux";
import { setIsNewUser, setUser } from "../../features/auth/user.reducer";
import {
  getProfileCompletionState,
  isProfileIncomplete,
} from "../../features/auth/profileCompletion";
import {
  PARENT_BOOKING_HUB_PATH,
  clearParentPostAuthRedirect,
  persistParentSessionMode,
  persistParentPostAuthRedirect,
  readParentPostAuthRedirect,
} from "../../features/parentFlow/parentFlow";
import {
  setDatadogUserContext,
  startJourney,
  logParentLoginApiResult,
  logParentLoginPageViewed,
  logParentLoginSubmitted,
  logParentPostLoginRoutingDecision,
} from "../../utils/datadogLogger";

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const ParentLoginPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const redirectPath =
    location.state?.redirectTo ||
    readParentPostAuthRedirect() ||
    PARENT_BOOKING_HUB_PATH;

  useEffect(() => {
    persistParentPostAuthRedirect(redirectPath);
  }, [redirectPath]);

  useEffect(() => {
    logParentLoginPageViewed(redirectPath || null);
  }, [redirectPath]);

  const loginMutation = useMutation({
    mutationFn: (payload) => apiService.post("api/login", payload),
    onSuccess: (response) => {
      const profileData = response?.data?.data || {};
      logParentLoginApiResult(
        profileData?.email || formData.email.trim(),
        response?.status || 200,
        response?.data || {}
      );
      persistParentSessionMode();
      dispatch(setUser({ user: profileData, token: profileData.jwtoken }));
      setDatadogUserContext(profileData);

      if (isProfileIncomplete(profileData)) {
        logParentPostLoginRoutingDecision(
          profileData.id || profileData.user_id,
          profileData.email,
          "/afterlogin",
          "incomplete_profile",
          redirectPath || null
        );
        dispatch(setIsNewUser(true));
        navigate("/afterlogin", {
          replace: true,
          state: {
            loginData: profileData,
            showNameTab: getProfileCompletionState(profileData).isNameMissing,
            showDobTab: getProfileCompletionState(profileData).isDobMissing,
            showAvatarTab: getProfileCompletionState(profileData).isAvatarMissing,
            redirectAfterComplete: redirectPath,
          },
        });
        return;
      }

      logParentPostLoginRoutingDecision(
        profileData.id || profileData.user_id,
        profileData.email,
        redirectPath,
        "complete_profile",
        redirectPath || null
      );
      clearParentPostAuthRedirect();
      navigate(redirectPath, { replace: true });
    },
    onError: (error) => {
      logParentLoginApiResult(
        formData.email.trim(),
        error?.response?.status || 500,
        error?.response?.data || {},
        error?.response?.data?.message || error?.message || "Parent login failed."
      );
      setErrors({
        submit:
          error?.response?.data?.message ||
          "Unable to log in with those student credentials.",
      });
    },
  });

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value.trimStart() }));
    setErrors((current) => ({ ...current, [name]: "", submit: "" }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = {};

    if (!formData.email) {
      nextErrors.email = "Email is required.";
    } else if (!validateEmail(formData.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!formData.password) {
      nextErrors.password = "Password is required.";
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    startJourney("parent_login");
    logParentLoginSubmitted(formData.email.trim(), redirectPath || null);
    loginMutation.mutate({
      email: formData.email.trim(),
      password: formData.password,
    });
  };

  const inputClassName =
    "w-full rounded-[22px] border border-[#d8cfbf] bg-[#fffaf4] px-4 py-3 text-base text-[#111827] outline-none transition placeholder:text-[#7c8799] focus:border-[#08059B] focus:bg-white";

  return (
    <ParentAuthLayout
      title="Parent Login"
      description="Sign in to continue with bookings and account access."
      accent="Quick access for parents"
      accentDescription="Stay ready to book sessions, manage your child's learning time, and unlock subscription benefits that support steady progress."
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#08059B]">
            Student credentials
          </p>
          <h2 className="mt-2 text-3xl font-bold text-[#111827]">
            Parent Login Page
          </h2>
        </div>
      </div>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
        <div>
          <label
            htmlFor="parent-login-email"
            className="mb-2 block text-sm font-bold text-[#374151]"
          >
            Student email
          </label>
          <input
            id="parent-login-email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            className={inputClassName}
            placeholder="student@email.com"
            autoComplete="email"
          />
          {errors.email ? (
            <p className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-red-700">
              <AlertCircle size={16} />
              {errors.email}
            </p>
          ) : null}
        </div>

        <div>
          <label
            htmlFor="parent-login-password"
            className="mb-2 block text-sm font-bold text-[#374151]"
          >
            Student password
          </label>
          <div className="relative">
            <input
              id="parent-login-password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleInputChange}
              className={`${inputClassName} pr-12`}
              placeholder="Enter password"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748b]"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.password ? (
            <p className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-red-700">
              <AlertCircle size={16} />
              {errors.password}
            </p>
          ) : null}
        </div>

        {errors.submit ? (
          <div className="rounded-[22px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {errors.submit}
          </div>
        ) : null}

        <div className="flex justify-end">
          <Link
            className="text-sm font-bold text-[#08059B] underline"
            to="/parent/forgot-password"
          >
            Forgot password?
          </Link>
        </div>

        <button
          type="submit"
          disabled={loginMutation.isPending}
          className="inline-flex w-full items-center justify-center rounded-full bg-[#08059B] px-6 py-3 text-lg font-bold text-white transition hover:bg-[#01044F] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {loginMutation.isPending ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={20} className="animate-spin" />
              Logging in...
            </span>
          ) : (
            "Continue as Parent"
          )}
        </button>
      </form>

      <div className="mt-8 rounded-[28px] border border-[#e6ded0] bg-[#fcfaf6] px-5 py-4 text-sm leading-7 text-[#475569]">
        Don&apos;t have a student account yet?{" "}
        <Link className="font-bold text-[#08059B] underline" to="/parent/signup">
          Create Parent Account
        </Link>
      </div>
    </ParentAuthLayout>
  );
};

export default ParentLoginPage;
